import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import contactService from "../services/contactService";
import { toast } from "react-toastify";

/**
 * Create a new contact
 */
export const createContact = createAsyncThunk(
  "contact/create",
  async (contactData, { rejectWithValue }) => {
    try {
      const response = await contactService.createContact(contactData);
      toast.success("Contact created successfully");
      return response.contact;
    } catch (error) {
      toast.error(error.message || "Failed to create contact");
      return rejectWithValue(error.message || "Failed to create contact");
    }
  }
);

/**
 * Get all contacts
 */
export const getAllContacts = createAsyncThunk(
  "contact/getAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await contactService.getAllContacts(params);
      return response;
    } catch (error) {
      toast.error(error.message || "Failed to fetch contacts");
      return rejectWithValue(error.message || "Failed to fetch contacts");
    }
  }
);

/**
 * Get single contact by ID
 */
export const getContactById = createAsyncThunk(
  "contact/getById",
  async (contactId, { rejectWithValue }) => {
    try {
      const response = await contactService.getContactById(contactId);
      return response.contact;
    } catch (error) {
      toast.error(error.message || "Failed to fetch contact");
      return rejectWithValue(error.message || "Failed to fetch contact");
    }
  }
);

/**
 * Update contact
 */
export const updateContact = createAsyncThunk(
  "contact/update",
  async ({ contactId, contactData }, { rejectWithValue }) => {
    try {
      const response = await contactService.updateContact(
        contactId,
        contactData
      );
      toast.success("Contact updated successfully");
      return response.contact;
    } catch (error) {
      toast.error(error.message || "Failed to update contact");
      return rejectWithValue(error.message || "Failed to update contact");
    }
  }
);

/**
 * Delete contact
 */
export const deleteContact = createAsyncThunk(
  "contact/delete",
  async (contactId, { rejectWithValue }) => {
    try {
      await contactService.deleteContact(contactId);
      toast.success("Contact deleted successfully");
      return contactId;
    } catch (error) {
      toast.error(error.message || "Failed to delete contact");
      return rejectWithValue(error.message || "Failed to delete contact");
    }
  }
);

/**
 * Get active contacts
 */
export const getActiveContacts = createAsyncThunk(
  "contact/getActive",
  async (_, { rejectWithValue }) => {
    try {
      const response = await contactService.getActiveContacts();
      return response.contacts;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch active contacts");
    }
  }
);

// Initial state
const initialState = {
  contacts: [],
  currentContact: null,
  activeContacts: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalContacts: 0,
  },
  loading: false,
  error: null,
  createLoading: false,
  createSuccess: false,
  updateLoading: false,
  updateSuccess: false,
  activeContactsLoading: false,
};

// Slice
const contactSlice = createSlice({
  name: "contact",
  initialState,
  reducers: {
    clearCurrentContact: (state) => {
      state.currentContact = null;
    },
    resetCreate: (state) => {
      state.createSuccess = false;
    },
    resetUpdate: (state) => {
      state.updateSuccess = false;
    },
    clearErrors: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Contact
      .addCase(createContact.pending, (state) => {
        state.createLoading = true;
        state.createSuccess = false;
        state.error = null;
      })
      .addCase(createContact.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createSuccess = true;
        state.contacts = [action.payload, ...state.contacts];
        state.error = null;
      })
      .addCase(createContact.rejected, (state, action) => {
        state.createLoading = false;
        state.createSuccess = false;
        state.error = action.payload;
      })
      // Get All Contacts
      .addCase(getAllContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts = action.payload.contacts;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getAllContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Contact By ID
      .addCase(getContactById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getContactById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentContact = action.payload;
        state.error = null;
      })
      .addCase(getContactById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Contact
      .addCase(updateContact.pending, (state) => {
        state.updateLoading = true;
        state.updateSuccess = false;
        state.error = null;
      })
      .addCase(updateContact.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = true;
        state.contacts = state.contacts.map((contact) =>
          contact._id === action.payload._id ? action.payload : contact
        );
        state.currentContact = action.payload;
        state.error = null;
      })
      .addCase(updateContact.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = false;
        state.error = action.payload;
      })
      // Delete Contact
      .addCase(deleteContact.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteContact.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts = state.contacts.filter(
          (contact) => contact._id !== action.payload
        );
        state.error = null;
      })
      .addCase(deleteContact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Active Contacts
      .addCase(getActiveContacts.pending, (state) => {
        state.activeContactsLoading = true;
        state.error = null;
      })
      .addCase(getActiveContacts.fulfilled, (state, action) => {
        state.activeContactsLoading = false;
        state.activeContacts = action.payload;
        state.error = null;
      })
      .addCase(getActiveContacts.rejected, (state, action) => {
        state.activeContactsLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentContact, resetCreate, resetUpdate, clearErrors } =
  contactSlice.actions;
export default contactSlice.reducer;

